<template>
   <pb-body-base class="extended-search" :busy="busy">
      <template v-slot:default>
         <pb-body>
            <template slot="title">
               <b-row class="mt-7 mb-4">
                  <b-col class="text-center">
                     <h3>Extended Search</h3>
                     <hr/>
                  </b-col>
               </b-row>
            </template>
            <template slot="body">
               <b-container>
                  <b-tabs v-model="activeTab" content-class="mt-3">
                     <!-- Article Number Tab -->
                     <b-tab title="Article Number Search" active>
                        <b-form @submit.prevent="searchArticle" class="mb-4">
                           <b-input-group>
                              <b-form-input
                                  style="background-color: #F1F1F1; border: none;"
                                  size="lg"
                                  id="article-number"
                                  v-model="articleSearchTerm"
                                  placeholder="e.g. 2354220 Brass"
                                  :state="articleSearchState"
                                  class="primary search-input"
                              ></b-form-input>
                              <b-input-group-append>
                                 <b-input-group-text>

                                    <b-spinner small v-if="isSearchingArticle"></b-spinner>
                                    <span v-else style="cursor:pointer;" @click="searchArticle"
                                          class="icon-search"></span>
                                 </b-input-group-text>
                              </b-input-group-append>
                              <b-form-invalid-feedback>
                                 Please provide at least the first 6 digits of the article number.
                              </b-form-invalid-feedback>
                           </b-input-group>
                        </b-form>
                        <!-- Article Search Results -->
                        <div v-if="articleSearchResults && articleSearchResults.length > 0">
                           <b-row>
                              <div
                                  v-for="(article, key) in articleSearchResults"
                                  :key="key"
                                  class="col-md-4 col-lg-2dot4 article-link mb-2 card-deck"
                                  @click="showArticle(article)"
                              >
                                 <pb-card :img-info="article.name">
                                    <template slot="image">
                                       <img :src="imageResource((article.webImage || article.image), 240)" alt="thumbnail"/>
                                    </template>
                                    <template slot="content">
                                       <span class="d-block">{{ article.primaryDescription || article.secondaryDescription }}</span>
                                       <span class="d-block"><small>{{ article.name }}</small></span>
                                    </template>
                                 </pb-card>
                              </div>
                           </b-row>
                        </div>
                        <div v-else-if="articleNoResults" class="text-left p-2">
                           No articles found matching your search.
                        </div>
                     </b-tab>

                     <!-- AI Image Search Tab (MOOD_SEARCH permission required) -->
                     <b-tab v-if="canAccessMoodSearch" title="Mood Search">
                        <b-form @submit.prevent="performAiSearch" class="mb-4">
                           <b-row>
                              <b-col md="12" class="mb-3">
                                 <b-input-group>
                                    <b-form-input
                                        style="background-color: #F1F1F1; border: none;"
                                        size="lg"
                                        id="ai-description"
                                        v-model="aiSearchDescription"
                                        placeholder="Enter a description (e.g. blue ceramic vase)"
                                        class="primary search-input"
                                    ></b-form-input>
                                    <b-input-group-text>

                                       <b-spinner small v-if="isSearchingAi"></b-spinner>
                                       <span v-else style="cursor:pointer;" @click="performAiSearch"
                                             class="icon-search"></span>
                                    </b-input-group-text>
                                 </b-input-group>
                              </b-col>
                           </b-row>
                           <b-row>
                              <b-col md="6">
                                 <label for="limit" class="small">Result Limit</label>
                                 <b-form-select
                                     id="limit"
                                     v-model="aiLimit"
                                     :options="[5, 10, 20, 50, 100]"
                                     size="sm"
                                 ></b-form-select>
                              </b-col>
                           </b-row>
                        </b-form>

                        <!-- AI Search Results -->
                        <div v-if="aiSearchResults && aiSearchResults.length > 0">
                           <b-row>
                              <div
                                  v-for="result in aiSearchResults"
                                  :key="result.id"
                                  class="col-md-4 col-lg-2dot4 article-link mb-2 card-deck"
                                  @click="showResultDetails(result)"
                              >
                                 <pb-card>
                                    <template slot="image">
                                       <div class="score-badge">{{ formatScore(result.similarity) }}</div>
                                       <img :src="result.imageUrl" alt="thumbnail"/>
                                    </template>
                                    <template slot="content">
                                       <span class="d-block">{{ result.articleName }}</span>
                                    </template>
                                 </pb-card>
                              </div>
                           </b-row>
                        </div>
                        <div v-if="isSearchingAi" class="text-center my-4">
                           <b-spinner variant="primary"></b-spinner>
                           <p class="mt-2">Searching...</p>
                        </div>
                        <b-alert v-else-if="aiNoResults" show variant="info">
                           No results found matching your criteria.
                        </b-alert>
                        <b-alert v-if="aiError" show variant="danger" class="mt-2">
                           {{ aiError }}
                        </b-alert>

                        <!-- Details Modal -->
                        <b-modal
                            v-model="showDetailsModal"
                            title="Image Details"
                            size="lg"
                            hide-footer
                        >
                           <template v-if="selectedResult">
                              <div class="d-flex mb-3">
                                 <b-img
                                     v-if="selectedResult.imageUrl"
                                     :src="selectedResult.imageUrl"
                                     fluid
                                     thumbnail
                                     class="details-image mr-3"
                                 ></b-img>
                                 <div>
                                    <p class="mb-2"><strong>ID:</strong> {{ selectedResult.articleId }}</p>
                                    <p class="mb-2"><strong>Similarity Score:</strong> {{ formatScore(selectedResult.similarity) }}</p>
                                    <p class="mb-2"><strong>Description:</strong></p>
                                    <p class="description-text">{{ selectedResult.description }}</p>
                                    <b-button
                                        variant="primary"
                                        size="sm"
                                        class="mt-3"
                                        @click="navigateToArticle(selectedResult.articleId)"
                                    >
                                       Go to Article
                                    </b-button>
                                 </div>
                              </div>
                           </template>
                        </b-modal>
                     </b-tab>
                  </b-tabs>
               </b-container>

               <!-- Article Link Selection Modal -->
               <b-modal ref="linkListModal" hide-footer centered size="md" title="Available in...">
                  <template #modal-header="{ close }">
                     <b-row class="align-items-center">
                        <b-col md="3">
                           <img v-if="selectedArticle" class="header-img"
                                :src="imageResource(selectedArticle.webImage, 100)" alt="image"/>
                        </b-col>
                        <b-col>
                           <h4 class="m-0 px-2">Available in...</h4>
                        </b-col>
                     </b-row>
                  </template>
                  <ul>
                     <li :key="key" v-for="(item, key) in linkList">
                        <router-link
                            style="font-size: 14px;"
                            @click.native="$refs.linkListModal.hide()"
                            :to="item.target"
                            class="d-block my-1"
                        >
                           {{ item.name }}
                        </router-link>
                     </li>
                  </ul>
               </b-modal>
            </template>
         </pb-body>
      </template>
   </pb-body-base>
</template>

<script>
import {mapGetters} from "vuex";
import ArticleApi from "@shared/_api/article";

export default {
   name: 'extended-search',
   data() {
      return {
         busy: false,
         activeTab: 0,
         // Article search
         articleSearchTerm: '',
         articleSearchResults: [],
         isSearchingArticle: false,
         articleNoResults: false,
         selectedArticle: null,
         // AI search
         aiSearchDescription: '',
         aiLimit: 10,
         aiSearchResults: [],
         isSearchingAi: false,
         aiNoResults: false,
         aiError: null,
         showDetailsModal: false,
         selectedResult: null
      };
   },
   computed: {
      ...mapGetters("account", ["canRead"]),
      canAccessMoodSearch() {
         return this.canRead('MOOD_SEARCH');
      },
      articleSearchState() {
         if (!this.articleSearchTerm) return null;
         return /^\d{6}\d*$/.test(this.articleSearchTerm);
      },
      linkList() {
         let articleLinks = [];
         if (this.selectedArticle) {
            if (this.selectedArticle.publicArticle) {
               articleLinks.push({
                  target: {
                     name: 'ar',
                     params: {articleId: this.selectedArticle.id}
                  },
                  name: 'Basic Shop'
               });
            }

            this.selectedArticle.articleCategoryList.forEach(articleCategory => {
               if (articleCategory.type === 'CUSTOMER_SHOP') {
                  articleLinks.push({
                     target: {
                        name: 'ar',
                        params: {articleId: this.selectedArticle.id, categoryId: articleCategory.id}
                     },
                     name: (articleCategory.webName || articleCategory.name)
                  });
               } else {
                  articleLinks.push({
                     target: {
                        name: 'customer',
                        params: {id: articleCategory.id, searchString: this.articleSearchTerm}
                     },
                     name: (articleCategory.webName || articleCategory.name)
                  });
               }
            });

            if (articleLinks.length === 0) {
               articleLinks.push({
                  target: {
                     name: 'basic',
                     params: {articleId: this.selectedArticle.id}
                  },
                  name: 'Open Details'
               });
            }
         }
         return articleLinks;
      }
   },
   methods: {
      searchArticle() {
         if (!this.articleSearchState) return;

         this.isSearchingArticle = true;
         this.articleSearchResults = [];
         this.articleNoResults = false;

         ArticleApi.lookupArticle(this.articleSearchTerm)
             .then(res => {
                if (!res || res.length === 0) {
                   this.articleNoResults = true;
                } else {
                   this.articleSearchResults = this.$_.orderBy(res, ['webImage', 'primaryDescription'], ['asc', 'asc']);
                }
             })
             .catch(() => {
                this.articleNoResults = true;
             })
             .finally(() => {
                this.isSearchingArticle = false;
             });
      },
      showArticle(article) {
         this.selectedArticle = article;
         if (this.linkList.length === 1) {
            this.$router.push(this.linkList[0].target);
         } else {
            this.$refs.linkListModal.show();
         }
      },
      async performAiSearch() {
         this.isSearchingAi = true;
         this.aiSearchResults = [];
         this.aiNoResults = false;
         this.aiError = null;

         try {
            const results = await ArticleApi.shopSearchImages(
                this.aiSearchDescription,
                {
                   limit: this.aiLimit
                }
            );

            if (!results || results.length === 0) {
               this.aiNoResults = true;
            } else {
               this.aiSearchResults = results;
            }
         } catch (error) {
            if (error.response && error.response.data) {
               this.aiError = typeof error.response.data === 'string'
                   ? error.response.data
                   : 'Search failed. Please try again.';
            } else {
               this.aiError = 'Search failed. Please try again.';
            }
         } finally {
            this.isSearchingAi = false;
         }
      },
      formatScore(score) {
         return (score * 100).toFixed(1) + '%';
      },
      showResultDetails(result) {
         this.selectedResult = result;
         this.showDetailsModal = true;
      },
      navigateToArticle(articleId) {
         this.showDetailsModal = false;
         this.$router.push({name: 'ar', params: {articleId: articleId}});
      }
   }
};
</script>

<style lang="scss" scoped>
@import "~@shared/_assets/scss/constants";

.header-img {
   max-height: 50px;
   flex: 1 0 0;
   width: 100%;
   height: 100%;
   margin: auto;
   object-fit: contain;
}

.search-input {
   background-color: #F1F1F1;
   border: none;
}

.details-image {
   max-width: 300px;
   max-height: 300px;
   object-fit: contain;
}

.description-text {
   white-space: pre-wrap;
}

.article-link {
   cursor: pointer;
}

.card-deck {
   .score-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: $color-primary;
      color: white;
      padding: 4px 8px;
      border-radius: 3px;
      font-size: 12px;
      font-weight: 500;
      z-index: 10;
   }
}
</style>
